import type { Editor, JSONContent } from "@tiptap/react";
import type { ParseError } from "@/parser/templateParser";
import { UnifiedTiptapEditor } from "@/components/unified/UnifiedTiptapEditor";
import { UnifiedTOC } from "@/components/unified/UnifiedToc";
import type { SubTemplateData } from "@/types/evaluation";
import { FloatInsertMenu } from "@/components/editor/FloatInsertMenu";

interface TocEntry {
  text: string;
  index: number;
  isSubTemplate: boolean;
  isDisabled: boolean;
  level: number;
}
interface ReportSection {
  sectionId?: string;
  lines: string[];
}
interface FloatBtnState {
  top: number;
  left: number;
  visible: boolean;
  insertPos: number;
}
interface TocPopoverPos {
  bottom: number;
  left: number;
}
interface EditorBodyProps {
  mode: "build" | "fill";
  showSource: boolean;
  showReport: boolean;
  unifiedDocument: JSONContent | null;
  values: Record<string, unknown>;
  variableValues: Record<string, string>;
  subTemplateValues: Record<string, SubTemplateData>;
  sectionToggles: Record<string, boolean>;
  collapsedSections: Record<string, boolean>;
  fieldToggles: Record<string, boolean>;
  interpolate: (s: string) => string;
  parseErrors: ParseError[];
  tocEntries: TocEntry[];
  activeHeadingIndex: number;
  reportSections: ReportSection[];
  sectionIds: string[];
  allCollapsed: boolean;
  codeMirrorContainerRef: React.RefObject<HTMLDivElement | null>;
  reportPaneRef: React.RefObject<HTMLDivElement | null>;
  editorRef: React.RefObject<Editor | null>;
  floatBtnElRef: React.RefObject<HTMLButtonElement | null>;
  hideTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  pendingInsertPosRef: React.RefObject<number | null>;
  tiptapEditingRef: React.RefObject<boolean>;
  tiptapEditingTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  tocBtnRef: React.RefObject<HTMLButtonElement | null>;
  setEditorWrapperEl: (el: HTMLDivElement | null) => void;
  setFieldValue: (fieldId: string, value: unknown) => void;
  setSubTemplateValue: (fieldId: string, data: SubTemplateData) => void;
  handleToggleSection: (sectionId: string) => void;
  handleCollapseSection: (sectionId: string) => void;
  handleToggleField: (fieldId: string) => void;
  handleTocNavigate: (idx: number) => void;
  handleCollapseAll: () => void;
  handleExpandAll: () => void;
  setUnifiedDocument: React.Dispatch<React.SetStateAction<JSONContent | null>>;
  syncDocToMd: (doc: JSONContent) => void;
  setVariableValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  floatBtn: FloatBtnState;
  setFloatBtn: React.Dispatch<React.SetStateAction<FloatBtnState>>;
  floatMenuOpen: boolean;
  setFloatMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowInsertPicker: React.Dispatch<React.SetStateAction<boolean>>;
  tocPopoverOpen: boolean;
  setTocPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  tocPopoverPos: TocPopoverPos | null;
  setTocPopoverPos: React.Dispatch<React.SetStateAction<TocPopoverPos | null>>;
  variables: string[];
}

function VariablesBar({
  setVariableValues,
  variables,
  variableValues,
}: Readonly<Pick<EditorBodyProps, "variables" | "variableValues" | "setVariableValues">>) {
  if (variables.length === 0) {
    return null;
  }
  return (
    <div
      className="variables-bar"
      style={{ borderBottom: "1px solid var(--border)", padding: "8px 16px" }}
    >
      {variables.map((v) => (
        <label key={v} className="variable-input">
          <span>{v}</span>
          <input
            type="text"
            value={variableValues[v] ?? ""}
            onChange={(e) => {
              setVariableValues((prev) => ({ ...prev, [v]: e.target.value }));
            }}
            placeholder={`{${v}}`}
          />
        </label>
      ))}
    </div>
  );
}
function SourcePane({
  codeMirrorContainerRef,
  parseErrors,
  showSource,
}: Readonly<Pick<EditorBodyProps, "showSource" | "codeMirrorContainerRef" | "parseErrors">>) {
  return (
    <div className="source-pane" style={{ display: showSource ? "" : "none" }}>
      <div ref={codeMirrorContainerRef} className="editor-codemirror" style={{ height: "100%" }} />
      {parseErrors.length > 0 && (
        <div className="editor-errors">
          {parseErrors.map((err, i) => (
            <div key={i} className="editor-error">
              Line {err.line}: {err.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function EditorFooter(props: Readonly<EditorBodyProps>) {
  const { allCollapsed, handleCollapseAll, handleExpandAll, sectionIds, tocEntries } = props;
  if (sectionIds.length === 0 && tocEntries.length === 0) {
    return null;
  }
  return (
    <div className="editor-footer">
      {sectionIds.length > 0 && (
        <button
          className="editor-footer-btn"
          onClick={allCollapsed ? handleExpandAll : handleCollapseAll}
        >
          {allCollapsed ? "\u25B6 Expand All" : "\u25BC Collapse All"}
        </button>
      )}
      {tocEntries.length > 0 && <TocFooter {...props} />}
    </div>
  );
}
function TocFooter({
  activeHeadingIndex,
  handleTocNavigate,
  setTocPopoverOpen,
  setTocPopoverPos,
  tocBtnRef,
  tocEntries,
  tocPopoverOpen,
  tocPopoverPos,
}: Readonly<EditorBodyProps>) {
  return (
    <div className="toc-footer-wrapper">
      <button
        ref={tocBtnRef}
        className="editor-footer-btn toc-footer-btn"
        onClick={() => {
          setTocPopoverOpen((p) => {
            if (!p && tocBtnRef.current) {
              const rect = tocBtnRef.current.getBoundingClientRect();
              setTocPopoverPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
            }
            return !p;
          });
        }}
      >
        \u2630 Contents
      </button>
      {tocPopoverOpen && tocPopoverPos && (
        <div
          className="toc-popover"
          style={{ bottom: tocPopoverPos.bottom, left: tocPopoverPos.left }}
        >
          <UnifiedTOC
            entries={tocEntries}
            activeIndex={activeHeadingIndex}
            onNavigate={handleTocNavigate}
          />
        </div>
      )}
    </div>
  );
}
function ReportPane({
  reportPaneRef,
  reportSections,
  showReport,
}: Readonly<Pick<EditorBodyProps, "showReport" | "reportPaneRef" | "reportSections">>) {
  if (!showReport) {
    return null;
  }
  return (
    <div className="report-pane">
      <div className="report-text" ref={reportPaneRef}>
        {reportSections
          .filter((s) => s.lines.length > 0)
          .map((section, i) => (
            <div key={i} data-section-id={section.sectionId}>
              {section.lines.map((line, j) => (
                <div key={j} className="report-line">
                  {line || "\u00A0"}
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}
function TiptapColumn(props: Readonly<EditorBodyProps>) {
  return (
    <div className="editor-column">
      <div className="unified-editor-wrapper" ref={props.setEditorWrapperEl}>
        <UnifiedTiptapEditor
          initialContent={props.unifiedDocument ?? { content: [], type: "doc" }}
          values={props.values}
          setFieldValue={props.setFieldValue}
          fillVariables={props.variableValues}
          interpolate={props.interpolate}
          subTemplateValues={props.subTemplateValues}
          setSubTemplateValue={props.setSubTemplateValue}
          sectionToggles={props.sectionToggles}
          onToggleSection={props.handleToggleSection}
          collapsedSections={props.collapsedSections}
          onCollapseSection={props.handleCollapseSection}
          fieldToggles={props.fieldToggles}
          onToggleField={props.handleToggleField}
          onDocumentChange={(doc) => {
            props.tiptapEditingRef.current = true;
            clearTimeout(props.tiptapEditingTimerRef.current);
            props.tiptapEditingTimerRef.current = setTimeout(() => {
              props.tiptapEditingRef.current = false;
            }, 150);
            props.setUnifiedDocument(doc);
            props.syncDocToMd(doc);
          }}
          onEditorReady={(editor) => {
            props.editorRef.current = editor;
          }}
        />
      </div>
      <EditorFooter {...props} />
    </div>
  );
}
function BodyLayout(props: Readonly<EditorBodyProps>) {
  return (
    <div className={`unified-body ${props.showReport || props.showSource ? "has-report" : ""}`}>
      <UnifiedTOC
        entries={props.tocEntries}
        activeIndex={props.activeHeadingIndex}
        onNavigate={props.handleTocNavigate}
      />
      <SourcePane
        showSource={props.showSource}
        codeMirrorContainerRef={props.codeMirrorContainerRef}
        parseErrors={props.parseErrors}
      />
      <TiptapColumn {...props} />
      <ReportPane
        showReport={props.showReport}
        reportPaneRef={props.reportPaneRef}
        reportSections={props.reportSections}
      />
    </div>
  );
}
export function EditorBody(props: Readonly<EditorBodyProps>) {
  return (
    <>
      <VariablesBar
        variables={props.variables}
        variableValues={props.variableValues}
        setVariableValues={props.setVariableValues}
      />
      <FloatInsertMenu
        floatBtn={props.floatBtn}
        setFloatBtn={props.setFloatBtn}
        floatBtnElRef={props.floatBtnElRef}
        hideTimerRef={props.hideTimerRef}
        pendingInsertPosRef={props.pendingInsertPosRef}
        floatMenuOpen={props.floatMenuOpen}
        setFloatMenuOpen={props.setFloatMenuOpen}
        setShowInsertPicker={props.setShowInsertPicker}
        editorRef={props.editorRef}
      />
      <BodyLayout {...props} />
    </>
  );
}
